import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { AlertCircle } from 'lucide-react';
import type { Book, BookStatus } from '../../../shared/types/index.js';
import { api } from '../../../shared/api/api.js';
import { useAuthStore } from '../../auth/store/useAuthStore.js';
import { useModalStore } from '../../../shared/store/useModalStore.js';
import { useNotificationStore } from '../../notifications/store/useNotificationStore.js';
import { ExportCsvModal } from '../../dashboard/components/ExportCsvModal.js';
import { BulkImportModal } from './BulkImportModal.js';
import { UibnSubmissionFlowGuideModal } from './UibnSubmissionFlowGuideModal.js';
import { PublisherAccountVerificationModal } from '../../auth/components/PublisherAccountVerificationModal.js';
import { PublisherVerificationBanner } from '../../auth/components/PublisherVerificationBanner.js';
import { BookLabelPrintModal } from './BookLabelPrintModal.js';
import { canEditBookMetadata } from './book-list/bookListUtils.js';
import {
  BookListHeader,
  RoleScopingBanner,
  BookSearchAutocomplete,
  BookFilterToolbar,
  BookListToolbar,
  BookCard,
  BookTableRow,
  BookListEmpty,
  BookListLoadMore,
} from './book-list/index.js';

export const BookList: React.FC = () => {
  const queryClient = useQueryClient();
  const { user } = useAuthStore();
  const { showToast } = useNotificationStore();

  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [searchKeyword, setSearchKeyword] = useState<string>('');
  const [searchTarget, setSearchTarget] = useState<'all' | 'title' | 'author'>('all');
  const [debouncedKeyword, setDebouncedKeyword] = useState<string>('');
  const [scopeMode, setScopeMode] = useState<'me' | 'public'>('me');

  // Debounce search keyword for background server sync without blocking instant UI filtering
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedKeyword(searchKeyword);
    }, 250);
    return () => clearTimeout(timer);
  }, [searchKeyword]);

  // Modals from global store
  const {
    viewingBook,
    editingBook,
    setViewingBook,
    setCertBook,
    setTreeBook,
    setEditingBook,
    setIsFormOpen,
    setDeletingBook,
  } = useModalStore();

  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [isFlowGuideOpen, setIsFlowGuideOpen] = useState(false);
  const [isPublisherVerifyOpen, setIsPublisherVerifyOpen] = useState(false);
  const [printBook, setPrintBook] = useState<Book | null>(null);

  // Check if current user is an unverified publisher or organization
  const isPublisherUnverified = Boolean(
    (user?.role === 'penerbit' || user?.role === 'organisasi') &&
    !user?.isPublisherVerified &&
    user?.publisherVerificationStatus !== 'verified'
  );

  // Expandable Detail View state
  const [expandedBookIds, setExpandedBookIds] = useState<Record<string, boolean>>({});
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');

  const toggleExpand = (bookId: string) => {
    setExpandedBookIds((prev) => ({
      ...prev,
      [bookId]: !prev[bookId],
    }));
  };

  const expandAll = (bookList: Book[]) => {
    const all: Record<string, boolean> = {};
    bookList.forEach((b) => {
      all[b.id] = true;
    });
    setExpandedBookIds(all);
  };

  const collapseAll = () => {
    setExpandedBookIds({});
  };

  // TanStack Query to fetch books (scoped by role and user session)
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['books', user?.id, user?.role, scopeMode, selectedStatus, selectedCategory, debouncedKeyword],
    queryFn: () =>
      api.getBooks({
        status: selectedStatus !== 'all' ? selectedStatus : undefined,
        category: selectedCategory !== 'all' ? selectedCategory : undefined,
        search: debouncedKeyword.trim() || undefined,
        scope: scopeMode === 'public' ? 'public' : undefined,
      }),
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.deleteBook(id),
    onSuccess: (_data, deletedId) => {
      queryClient.invalidateQueries({ queryKey: ['books'] });
      queryClient.invalidateQueries({ queryKey: ['stats'] });
      if (viewingBook?.id === deletedId) {
        setViewingBook(null);
      }
      if (editingBook?.id === deletedId) {
        setEditingBook(null);
        setIsFormOpen(false);
      }
      showToast('Draft Dihapus', 'Data draft UIBN berhasil dihapus dari katalog', 'info');
    },
    onError: (err: any) => {
      showToast('Gagal Menghapus', err.message || 'Draft UIBN tidak dapat dihapus', 'error');
    },
  });

  // Status change mutation (Approve / Reject / Resubmit)
  const statusMutation = useMutation({
    mutationFn: ({ id, status, note }: { id: string; status: BookStatus; note?: string }) =>
      api.updateBookStatus(id, status, note),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['books'] });
      queryClient.invalidateQueries({ queryKey: ['stats'] });
      queryClient.invalidateQueries({ queryKey: ['notifications'] });

      const statusLabels: Record<string, string> = {
        draft: 'Draft (Revisi Naskah)',
        diajukan: 'Diajukan ke Admin UIBN',
        disetujui: 'Disetujui Resmi UIBN',
        ditolak: 'Pengajuan Ditolak',
        menunggu_penerbit: 'Menunggu Kurasi Penerbit',
      };

      showToast(
        'Status Berhasil Diperbarui',
        `Status naskah diubah menjadi: ${statusLabels[variables.status] || variables.status.toUpperCase()}`,
        'success'
      );
    },
    onError: (err: any) => {
      showToast(
        'Gagal Mengubah Status',
        err.message || 'Terjadi kesalahan saat memproses status naskah',
        'error'
      );
    },
  });

  const [visibleCount, setVisibleCount] = useState<number>(10);

  const allBooks = data?.books || [];

  // Instant in-memory client-side filtering for 0ms ultra-fast response
  const filteredBooks = React.useMemo(() => {
    if (!searchKeyword.trim()) return allBooks;
    const q = searchKeyword.trim().toLowerCase();
    return allBooks.filter((b) => {
      const matchTitle =
        (b.title || '').toLowerCase().includes(q) ||
        (b.originalTitle || '').toLowerCase().includes(q) ||
        (b.subtitle || '').toLowerCase().includes(q);
      const matchAuthor = (b.authorName || '').toLowerCase().includes(q);
      const matchUibn = (b.uibn || '').toLowerCase().includes(q);

      if (searchTarget === 'title') {
        return matchTitle;
      }
      if (searchTarget === 'author') {
        return matchAuthor;
      }
      return matchTitle || matchAuthor || matchUibn;
    });
  }, [allBooks, searchKeyword, searchTarget]);

  // Reset visibleCount whenever filters, search keyword, target, or scope change
  useEffect(() => {
    setVisibleCount(10);
  }, [selectedStatus, selectedCategory, searchKeyword, searchTarget, scopeMode]);

  const displayedBooks = filteredBooks.slice(0, visibleCount);

  const handleSelectBookDetail = async (bookId: string, fallbackTitle: string) => {
    try {
      const res = await api.getBookById(bookId);
      if (res.book) {
        setViewingBook(res.book);
      }
    } catch {
      setSearchKeyword(fallbackTitle);
    }
  };

  const handleDeleteBook = (id: string, title: string) => {
    setDeletingBook({ id, title });
  };

  const handleStatusChange = (id: string, status: BookStatus, note?: string) => {
    statusMutation.mutate({ id, status, note });
  };

  const handleAddBook = () => {
    if (isPublisherUnverified) {
      showToast(
        'Verifikasi Penerbit Diperlukan',
        'Hanya lembaga penerbit terverifikasi yang dapat mendaftarkan buku di UIBN. Silakan lengkapi formulir verifikasi berikut.',
        'error'
      );
      setIsPublisherVerifyOpen(true);
      return;
    }
    if (user) {
      localStorage.removeItem(`uibn_book_form_draft_${user.id}`);
    }
    localStorage.removeItem('uibn_book_form_draft');
    setEditingBook(null);
    setIsFormOpen(true);
  };

  const handleOpenImport = () => {
    if (isPublisherUnverified) {
      showToast(
        'Verifikasi Penerbit Diperlukan',
        'Hanya lembaga penerbit terverifikasi yang dapat mengimpor data buku di UIBN. Silakan lengkapi verifikasi terlebih dahulu.',
        'error'
      );
      setIsPublisherVerifyOpen(true);
      return;
    }
    setIsImportModalOpen(true);
  };

  return (
    <div className="space-y-6">
      {/* Top action header */}
      <BookListHeader
        user={user}
        onOpenExportModal={() => setIsExportModalOpen(true)}
        onOpenImportModal={handleOpenImport}
        onAddBook={handleAddBook}
        onOpenFlowGuide={() => setIsFlowGuideOpen(true)}
      />

      {/* Publisher Verification Alert Banner */}
      <PublisherVerificationBanner
        user={user}
        onOpenVerificationModal={() => setIsPublisherVerifyOpen(true)}
      />

      {/* Role-based Data Scoping Banner & Policy Notice */}
      <RoleScopingBanner
        user={user}
        scopeMode={scopeMode}
        onSetScopeMode={setScopeMode}
      />

      {/* Filter and search bar */}
      <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-2xs space-y-3">
        <div className="flex flex-col md:flex-row gap-3">
          <BookSearchAutocomplete
            searchKeyword={searchKeyword}
            onSearchKeywordChange={setSearchKeyword}
            onSelectBookDetail={handleSelectBookDetail}
            searchTarget={searchTarget}
            onSearchTargetChange={setSearchTarget}
          />

          <BookFilterToolbar
            selectedCategory={selectedCategory}
            onSelectCategory={setSelectedCategory}
            selectedStatus={selectedStatus}
            onSelectStatus={setSelectedStatus}
          />
        </div>

        {/* Real-time search status indicator banner */}
        {searchKeyword.trim() && (
          <div className="flex items-center justify-between gap-2 px-3.5 py-2 bg-indigo-50/70 border border-indigo-100 rounded-xl text-xs text-indigo-900">
            <div className="flex items-center gap-2 truncate">
              <span className="font-semibold truncate">
                Pencarian teks: <span className="font-bold text-indigo-700">&quot;{searchKeyword.trim()}&quot;</span>
              </span>
              <span className="text-[11px] text-indigo-700 bg-white px-2 py-0.5 rounded-md border border-indigo-200/60 shrink-0 font-medium">
                {searchTarget === 'title' ? 'Judul Buku' : searchTarget === 'author' ? 'Nama Penulis' : 'Judul & Penulis'}
              </span>
              <span className="text-[11px] text-slate-500 hidden sm:inline">
                • {filteredBooks.length} buku ditemukan
              </span>
            </div>
            <button
              type="button"
              onClick={() => setSearchKeyword('')}
              className="text-[11px] font-semibold text-indigo-600 hover:text-indigo-800 hover:underline shrink-0 cursor-pointer"
            >
              Reset
            </button>
          </div>
        )}
      </div>

      {/* Books Controls & View Mode Toolbar */}
      {!isLoading && !error && filteredBooks.length > 0 && (
        <BookListToolbar
          displayedBooks={displayedBooks}
          totalBooksCount={filteredBooks.length}
          selectedStatus={selectedStatus}
          expandedBookIds={expandedBookIds}
          viewMode={viewMode}
          onExpandAll={() => expandAll(displayedBooks)}
          onCollapseAll={collapseAll}
          onSetViewMode={setViewMode}
        />
      )}

      {/* Book list container */}
      {isLoading ? (
        <div className="p-12 text-center bg-white rounded-2xl border border-slate-200">
          <div className="inline-block w-6 h-6 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin mb-3" />
          <p className="text-xs text-slate-500">Memuat katalog buku...</p>
        </div>
      ) : error ? (
        <div className="p-8 text-center bg-white rounded-2xl border border-slate-200">
          <AlertCircle className="w-8 h-8 text-rose-500 mx-auto mb-2" />
          <p className="text-sm font-semibold text-slate-800">Gagal memuat katalog buku</p>
          <button
            onClick={() => refetch()}
            className="mt-3 px-3 py-1.5 text-xs bg-indigo-50 text-indigo-600 rounded-lg font-medium cursor-pointer"
          >
            Coba Lagi
          </button>
        </div>
      ) : filteredBooks.length === 0 ? (
        <BookListEmpty
          onAddBook={handleAddBook}
          searchKeyword={searchKeyword}
          onClearSearch={() => setSearchKeyword('')}
        />
      ) : (
        <AnimatePresence mode="wait">
          {viewMode === 'grid' ? (
            <motion.div
              key="grid"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.15 }}
              className="grid grid-cols-1 gap-5 md:gap-6"
            >
              {displayedBooks.map((b, idx) => (
                <BookCard
                  key={b.id}
                  book={b}
                  index={idx}
                  isExpanded={Boolean(expandedBookIds[b.id])}
                  onToggleExpand={() => toggleExpand(b.id)}
                  onOpenModal={setViewingBook}
                  onOpenTree={setTreeBook}
                  onOpenCertificate={setCertBook}
                  onOpenPrintLabel={(b) => setPrintBook(b)}
                  onEdit={(bookToEdit) => {
                    if (!canEditBookMetadata(bookToEdit, user)) {
                      showToast('Akses Ditolak', 'Metadata naskah dikelola oleh Penulis. Penerbit tidak dapat mengedit metadata naskah yang diajukan Penulis.', 'error');
                      return;
                    }
                    setEditingBook(bookToEdit);
                    setIsFormOpen(true);
                  }}
                  onDelete={handleDeleteBook}
                  onUpdateStatus={handleStatusChange}
                  isStatusPending={statusMutation.isPending}
                  user={user}
                />
              ))}
            </motion.div>
          ) : (
            <motion.div
              key="list"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.15 }}
              className="space-y-3"
            >
              {displayedBooks.map((b, idx) => (
                <BookTableRow
                  key={b.id}
                  book={b}
                  index={idx}
                  isExpanded={Boolean(expandedBookIds[b.id])}
                  onToggleExpand={() => toggleExpand(b.id)}
                  onOpenModal={setViewingBook}
                  onOpenTree={setTreeBook}
                  onOpenCertificate={setCertBook}
                  onOpenPrintLabel={(b) => setPrintBook(b)}
                  onEdit={(bookToEdit) => {
                    if (!canEditBookMetadata(bookToEdit, user)) {
                      showToast('Akses Ditolak', 'Metadata naskah dikelola oleh Penulis. Penerbit tidak dapat mengedit metadata naskah yang diajukan Penulis.', 'error');
                      return;
                    }
                    setEditingBook(bookToEdit);
                    setIsFormOpen(true);
                  }}
                  onDelete={handleDeleteBook}
                  onUpdateStatus={handleStatusChange}
                  isStatusPending={statusMutation.isPending}
                  user={user}
                />
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      )}

      {/* Load More Button Banner */}
      {!isLoading && !error && filteredBooks.length > visibleCount && (
        <BookListLoadMore
          displayedCount={displayedBooks.length}
          totalCount={filteredBooks.length}
          onLoadMore={() => setVisibleCount((prev) => prev + 10)}
        />
      )}

      {/* Export CSV Modal with Date Range Filters */}
      <ExportCsvModal
        isOpen={isExportModalOpen}
        onClose={() => setIsExportModalOpen(false)}
      />

      <BulkImportModal
        isOpen={isImportModalOpen}
        onClose={() => setIsImportModalOpen(false)}
        onSuccess={() => {
          queryClient.invalidateQueries({ queryKey: ['books'] });
          queryClient.invalidateQueries({ queryKey: ['stats'] });
        }}
      />

      <UibnSubmissionFlowGuideModal
        isOpen={isFlowGuideOpen}
        onClose={() => setIsFlowGuideOpen(false)}
      />

      <PublisherAccountVerificationModal
        isOpen={isPublisherVerifyOpen}
        onClose={() => setIsPublisherVerifyOpen(false)}
        onSuccess={() => {
          queryClient.invalidateQueries({ queryKey: ['books'] });
          queryClient.invalidateQueries({ queryKey: ['stats'] });
        }}
      />

      {printBook && (
        <BookLabelPrintModal
          book={printBook}
          isOpen={Boolean(printBook)}
          onClose={() => setPrintBook(null)}
        />
      )}
    </div>
  );
};
